<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import ExportService from './ExportService.ts'
import { useRuntimeEnv } from "@piveau/piveau-hub-ui-modules"
import { extractApiParams } from './search-filters/ExportApiParams.ts'

const env = useRuntimeEnv()

const segmentToFilter = {
    datasets: 'dataset',
    catalogues: 'catalogue',
    combined: { filters: 'catalogue,dataset,resource', resource: 'editorial-content' },
    'editorial-content': { filters: 'resource', resource: 'editorial-content' },
    dataservices:'dataservice'
}

const props = defineProps({
    open: { type: Boolean, default: false },
})
const emit = defineEmits(['update:open'])

const route = useRoute()

const selectedNumber = ref(null)
const selectedFileType = ref(null)
const isExporting = ref(false)
const error = ref(null)
const touched = ref({ number: false, fileType: false })

const numbers = Array.from({ length: 10 }, (_, i) => (i + 1) * 100)
const fileTypes = ['CSV', 'JSON', 'TSV']

const errors = computed(() => ({
    number: touched.value.number && !selectedNumber.value ? 'Please select number of results.' : '',
    fileType: touched.value.fileType && !selectedFileType.value ? 'Please select a file type.' : '',
}))

const isFormValid = computed(() => !!selectedNumber.value && !!selectedFileType.value)

function close() {
    error.value = null
    touched.value = { number: false, fileType: false }
    selectedNumber.value = null
    selectedFileType.value = null
    emit('update:open', false)
}

function buildFacetsFromRoute(route, isCombined) {
    const toArray = (val) => val ? (Array.isArray(val) ? val : [val]) : []
    return {
        ...(isCombined && { dataScope: toArray(route.query.dataScope) }),
        country: toArray(route.query.country),
        catalog: toArray(route.query.catalog),
        format: toArray(route.query.format),
        license: toArray(route.query.license),
        categories: toArray(route.query.categories),
        publisher: toArray(route.query.publisher),
        subject: toArray(route.query.subject),
        keywords: toArray(route.query.keywords),
        is_hvd: toArray(route.query.is_hvd),
        hvdCategory: toArray(route.query.hvdCategory),
        source_type: toArray(route.query.source_type),
        superCatalog: toArray(route.query.superCatalog),
        mostLiked: toArray(route.query.mostLiked),
        content_type: toArray(route.query.content_type),
        scoring: toArray(route.query.scoring),
    }
}

async function exportData() {
    touched.value = { number: true, fileType: true }
    if (!isFormValid.value) return

    isExporting.value = true
    error.value = null

    try {
        const baseUrl = `${env.api.baseUrl}search`
        const lastSegment = route.path.split('/').filter(Boolean).pop() ?? ''
        const filter = segmentToFilter[lastSegment] ?? lastSegment
        const locale = route.query.locale || 'en'
        const isCombined = lastSegment === 'combined'

        const rawFacets = buildFacetsFromRoute(route, isCombined)

        const { facets, countryDataParam, scoringParams } = extractApiParams({
            baseUrl,
            route,
            config: {
                scope: lastSegment,
                apiFilter: null,
                apiQueryParams: null,
                apiLinkIncludes: null,
            },
            facets: rawFacets,
        })

        await ExportService.exportFromApi(
            baseUrl,
            {
                q: route.query.query || '',
                filter,
                limit: selectedNumber.value,
                sort: route.query.sort
                    ? decodeURIComponent(route.query.sort)
                    : 'relevance desc, modified desc, title.en asc',
                dataServices: route.query.dataServices || 'false',
                facets: JSON.stringify(facets),
                facetOperator: route.query.facetOperator || 'AND',
                facetGroupOperator: route.query.facetGroupOperator || 'AND',
                ...countryDataParam,
                ...scoringParams,
            },
            selectedFileType.value.toLowerCase(),
            locale
        )
        close()
    } catch (err) {
        error.value = 'Export failed: ' + err.message
    } finally {
        isExporting.value = false
    }
}
</script>

<template>
    <Teleport to="body">
        <template v-if="props.open">
            <div class="modal-overlay" @click="close" />
            <div class="modal-box">
                <h2 class="modal-title">Export Data</h2>
                <p class="modal-subtitle">Select the number of results and file type to export.</p>

                <div class="field-group">
                    <div class="field-row">
                        <label for="number" class="field-label">Results</label>
                        <select id="number" v-model="selectedNumber" @blur="touched.number = true"
                            :class="['field-select', { 'field-select--error': errors.number }]">
                            <option value="" disabled>Select number</option>
                            <option v-for="num in numbers" :key="num" :value="num">{{ num }}</option>
                        </select>
                    </div>
                    <p v-if="errors.number" class="field-error">{{ errors.number }}</p>
                </div>

                <div class="field-group">
                    <div class="field-row">
                        <label for="fileType" class="field-label">File Type</label>
                        <select id="fileType" v-model="selectedFileType" @blur="touched.fileType = true"
                            :class="['field-select', { 'field-select--error': errors.fileType }]">
                            <option value="" disabled>Select file type</option>
                            <option v-for="type in fileTypes" :key="type" :value="type">{{ type }}</option>
                        </select>
                    </div>
                    <p v-if="errors.fileType" class="field-error">{{ errors.fileType }}</p>
                </div>

                <p v-if="error" class="api-error">{{ error }}</p>

                <div class="modal-footer">
                    <button class="btn-export" @click="exportData" :disabled="isExporting"
                        :class="{ 'btn-export--loading': isExporting }">
                        {{ isExporting ? 'Exporting…' : 'Export' }}
                    </button>
                </div>

                <button class="btn-close" @click="close" aria-label="Close">✕</button>
            </div>
        </template>
    </Teleport>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
}

.modal-box {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    border-radius: 8px;
    padding: 25px;
    width: 90vw;
    max-width: 450px;
    z-index: 9999;
    box-shadow: 0 10px 38px rgba(0, 0, 0, 0.35);
}

.modal-title {
    margin: 0 0 8px;
    font-size: 17px;
    font-weight: 600;
}

.modal-subtitle {
    font-size: 14px;
    color: #666;
    margin: 0 0 20px;
}

.field-group {
    margin-bottom: 15px;
}

.field-row {
    display: flex;
    align-items: center;
    gap: 20px;
}

.field-label {
    width: 90px;
    text-align: right;
    font-size: 14px;
}

.field-select {
    flex: 1;
    height: 35px;
    border-radius: 8px;
    border: 1px solid #ccc;
    padding: 0 10px;
    font-size: 14px;
    outline: none;
}

.field-select--error {
    border-color: #dc2626;
}

.field-error {
    margin: 4px 0 0 110px;
    font-size: 12px;
    color: #dc2626;
}

.api-error {
    color: #dc2626;
    font-size: 13px;
    margin: 0 0 10px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 25px;
}

.btn-export {
    height: 35px;
    padding: 0 15px;
    border-radius: 8px;
    background: #0e47cb;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    border: none;
    cursor: pointer;
}

.btn-export--loading {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-close {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 16px;
}
</style>